/**
 * Team situation summary — where the team stands, what is fragile and what to
 * do over the next 30/60/90 days. Read on screen, then download the 2-page PDF.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Loader2, ArrowLeft, RefreshCw, FileDown, TrendingUp, TrendingDown,
  AlertTriangle, ShieldAlert, CheckCircle2, Eye, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr } from '../../../components/admin/billing/shared';
import { DriveIQBadge } from '../../../components/admin/DriveIQ';

const Kpi = ({ label, value, suffix = '', tone = 'text-slate-900', testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid={testid}>
    <div className="text-xs text-slate-500">{label}</div>
    <div className={`text-2xl font-semibold ${tone}`}>
      {value === null || value === undefined ? '—' : `${value}${suffix}`}
    </div>
  </div>
);

const Section = ({ title, icon: Icon, tone = 'text-teal-700', children, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
    <h2 className={`text-sm font-semibold ${tone} flex items-center gap-1.5 mb-2`}>
      <Icon size={15} /> {title}
    </h2>
    {children}
  </div>
);

const TeamAnalysisPage = () => {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async (refresh = false) => {
    if (refresh) setBusy('gen'); else setLoading(true);
    setErr('');
    try {
      const { data: d } = await axios.get(
        `${API}/performance/team-analysis${refresh ? '?refresh=true' : ''}`,
        { headers: authHeader(), timeout: 240000 });
      setData(d);
      if (refresh) toast.success('Summary refreshed from the latest records');
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); setBusy(''); }
  }, [authHeader]);

  useEffect(() => { load(false); }, [load]);

  const downloadPdf = async () => {
    setBusy('pdf');
    try {
      const res = await axios.get(`${API}/performance/team-analysis/pdf`,
        { headers: authHeader(), responseType: 'blob', timeout: 240000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `team_performance_${data?.dataset?.as_on || 'summary'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 p-8" data-testid="team-analysis-loading">
        <Loader2 size={18} className="animate-spin" /> Reading the team&apos;s records…
      </div>
    );
  }

  const ds = data?.dataset || {};
  const an = data?.analysis || {};
  const t = ds.totals || {};

  return (
    <div className="space-y-5 pb-24" data-testid="team-analysis-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/performance" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-1">
            <ArrowLeft size={14} /> Performance
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-semibold text-slate-900">Team situation summary</h1>
            <DriveIQBadge label="Prepared by" testId="team-analysis-driveiq" />
          </div>
          <p className="text-sm text-slate-500">
            {ds.headcount || 0} active member(s) · as on {ds.as_on || '—'}
            {ds.cycle ? ` · ${ds.cycle}` : ''}
            {data?.generated_at ? ` · prepared ${String(data.generated_at).replace('T', ' ').slice(0, 16)}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => load(true)} disabled={!!busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-50"
            data-testid="regenerate-analysis-btn"
          >
            {busy === 'gen' ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            {busy === 'gen' ? 'Re-reading records…' : 'Refresh'}
          </button>
          <button
            onClick={downloadPdf} disabled={!!busy || !data}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700 disabled:opacity-50"
            data-testid="download-analysis-pdf-btn"
          >
            {busy === 'pdf' ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />}
            Download PDF
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="team-analysis-error">
          {err}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi label="Team members" value={ds.headcount} testid="kpi-headcount" />
            <Kpi label="Avg skill index" value={t.avg_skill_index} suffix="%" tone="text-teal-700" testid="kpi-skill-index" />
            <Kpi label="Avg appraisal" value={t.avg_appraisal_score} testid="kpi-appraisal" />
            <Kpi label="Avg attendance" value={t.avg_attendance_pct} suffix="%"
                 tone={(t.avg_attendance_pct ?? 100) < 85 ? 'text-red-600' : 'text-emerald-700'}
                 testid="kpi-attendance" />
            <Kpi label="Open skill gaps" value={t.total_gaps}
                 tone={t.total_gaps ? 'text-amber-700' : 'text-emerald-700'} testid="kpi-gaps" />
          </div>

          {an.headline && (
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 text-base font-medium text-slate-900"
                 data-testid="analysis-headline">
              {an.headline}
            </div>
          )}

          <Section title="Where we stand" icon={Users} testid="section-where-we-stand">
            <p className="text-sm text-slate-700 leading-relaxed">{an.where_we_stand || '—'}</p>
          </Section>

          <div className="grid lg:grid-cols-2 gap-4">
            <Section title="What is working" icon={CheckCircle2} tone="text-emerald-700" testid="section-strengths">
              <ul className="space-y-2">
                {(an.strengths || []).map((s, i) => (
                  <li key={i} className="text-sm text-slate-700" data-testid={`strength-${i}`}>
                    <span className="font-medium text-slate-900">{s.title}</span> — {s.detail}
                  </li>
                ))}
                {!an.strengths?.length && <li className="text-sm text-slate-500">Nothing recorded.</li>}
              </ul>
            </Section>

            <Section title="Critical gaps" icon={AlertTriangle} tone="text-amber-700" testid="section-gaps">
              <ul className="space-y-2">
                {(an.critical_gaps || []).map((g, i) => (
                  <li key={i} className="text-sm text-slate-700" data-testid={`gap-${i}`}>
                    <span className="font-medium text-slate-900">{g.title}</span> — {g.detail}
                    {g.business_impact && (
                      <span className="block text-xs text-amber-800 mt-0.5">Impact: {g.business_impact}</span>
                    )}
                  </li>
                ))}
                {!an.critical_gaps?.length && <li className="text-sm text-slate-500">Nothing recorded.</li>}
              </ul>
            </Section>
          </div>

          {!!an.single_points_of_failure?.length && (
            <Section title="Single points of failure — one person deep" icon={ShieldAlert} tone="text-red-700"
                     testid="section-spof">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[520px]">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                      <th className="py-2 pr-3">Skill</th>
                      <th className="py-2 pr-3">Only person</th>
                      <th className="py-2">What happens if they are away</th>
                    </tr>
                  </thead>
                  <tbody>
                    {an.single_points_of_failure.map((s, i) => (
                      <tr key={i} className="border-b border-slate-100" data-testid={`spof-${i}`}>
                        <td className="py-2 pr-3 text-slate-800">{s.skill}</td>
                        <td className="py-2 pr-3 font-medium text-slate-900">{s.only_person}</td>
                        <td className="py-2 text-slate-600">{s.risk}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          <div className="grid lg:grid-cols-2 gap-4">
            <Section title="Improving" icon={TrendingUp} tone="text-emerald-700" testid="section-improving">
              <ul className="space-y-1.5">
                {(an.improving || []).map((p, i) => (
                  <li key={i} className="text-sm text-slate-700" data-testid={`improving-${i}`}>
                    <span className="font-medium text-slate-900">{p.name}</span> — {p.detail}
                  </li>
                ))}
                {!an.improving?.length && <li className="text-sm text-slate-500">Nobody yet.</li>}
              </ul>
            </Section>
            <Section title="Slipping" icon={TrendingDown} tone="text-red-700" testid="section-slipping">
              <ul className="space-y-1.5">
                {(an.slipping || []).map((p, i) => (
                  <li key={i} className="text-sm text-slate-700" data-testid={`slipping-${i}`}>
                    <span className="font-medium text-slate-900">{p.name}</span> — {p.detail}
                  </li>
                ))}
                {!an.slipping?.length && <li className="text-sm text-slate-500">Nobody.</li>}
              </ul>
            </Section>
          </div>

          <div className="grid lg:grid-cols-3 gap-4" data-testid="section-way-forward">
            {[
              ['Next 30 days', an.way_forward?.next_30_days, 'text-red-700', 'border-red-100'],
              ['Next 60 days', an.way_forward?.next_60_days, 'text-amber-700', 'border-amber-100'],
              ['Next 90 days', an.way_forward?.next_90_days, 'text-teal-700', 'border-teal-100'],
            ].map(([title, items, tone, border], n) => (
              <div key={title} className={`bg-white border ${border} rounded-xl p-4`} data-testid={`plan-window-${n}`}>
                <h3 className={`text-xs font-semibold uppercase tracking-wider ${tone} mb-2`}>{title}</h3>
                <ul className="space-y-2 list-disc list-inside">
                  {(items || []).map((a, i) => (
                    <li key={i} className="text-sm text-slate-700">{a}</li>
                  ))}
                  {!items?.length && <li className="text-sm text-slate-500 list-none">Nothing planned.</li>}
                </ul>
              </div>
            ))}
          </div>

          {!!an.owner_watchlist?.length && (
            <Section title="Owner's watchlist" icon={Eye} testid="section-watchlist">
              <ul className="space-y-1.5 list-disc list-inside">
                {an.owner_watchlist.map((w, i) => (
                  <li key={i} className="text-sm text-slate-700" data-testid={`watch-${i}`}>{w}</li>
                ))}
              </ul>
            </Section>
          )}

          <p className="text-xs text-slate-400">
            Prepared from the workshop&apos;s own skill assessments, appraisals and the last
            {` ${ds.window?.days || 90} `}days of attendance. Refresh after new assessments are signed off.
          </p>
        </>
      )}
    </div>
  );
};

export default TeamAnalysisPage;
